// @ts-strict-ignore
import { FC, useMemo } from 'react';

import { css } from '@emotion/react';
import { Box, styled } from '@mui/material';

import { OptionProps } from 'react-select';

import { useStores } from 'mobx/hooks/useStores';
import SettingsStore from 'mobx/stores/SettingsStore';

import {
  getGroupedTicketTypesForSelect,
  ITicketTypeSelectOptions,
  TicketSubTypeOption,
  TicketTypeFilterFn
} from 'utils/TicketType.utils';

import {
  defaultSymptomUrgencyValue,
  nurseReviewUrgency,
  attentionTodayUrgency,
  immediateAttentionUrgency,
  SymptomTicketUrgencyValueAndText,
  defaultNonSymptomUrgencyValue
} from 'utils/urgencyMappings';

import { AlertText } from 'constants/alertBadge.const';
import { FEATURES } from 'constants/features';

import {
  TICKET_TYPE_CALLBACK_ID,
  TICKET_TYPE_EPISODES_ID,
  TICKET_TYPE_NON_EPISODES_ID,
  TICKET_TYPE_ORAL_ONCO_OVERDUE_ID,
  TICKET_TYPE_ORAL_ONCO_REPORT_ID,
  TICKET_TYPE_OVERDUE_ID,
  TICKET_TYPE_SYMPTOM_ASSESSMENT_REPORT_ID
} from 'constants/itemTypes.const';

import { OperatorTicketUrgency, OperatorTicketUrgencyText } from 'constants/urgency.consts';

import OperatorTicket from 'models/OperatorTicket';

import { SearchableGroup, useSearchAndHighlight } from 'hooks/useSearchAndHighlight';
import { SearchAndHighlightResult } from 'hooks/useSearchAndHighlight.types';

import { HighlightBySearchResult } from 'components/Ticket/TicketRow/HighlightBySearchResultOrValue';

import { AlertBadge } from 'components/UIkit/atoms/AlertBadge';
import { DefaultMenuOption } from 'components/UIkit/atoms/Dropdown/Select/ReactSelectOptionComponent';
import { Text } from 'components/UIkit/atoms/Text';

// Extend the data type to include searchResult
export type TicketSubTypeOptionWithSearch = TicketSubTypeOption & {
  searchResult?: SearchAndHighlightResult;
};

export type TicketTypeSearchFilteredOptions = ITicketTypeSelectOptions & {
  options: TicketSubTypeOptionWithSearch[];
};

// get Ticket Types options
interface TicketTypesOptions {
  filterFn?: TicketTypeFilterFn;
  addCategoryAllOption?: boolean;
  addItemCategories?: boolean;
  itemCategoryIdsOrder?: ItemCategoryId[];

  addTicketTypesAndSubTypes?: boolean;
  showFullNameForNonSymptomTicketTypes?: boolean;
}

const ticketTypesOptionsDefault: TicketTypesOptions = {
  addCategoryAllOption: false,
  addItemCategories: false
};

// return true for active node OR included in operatorTicket types
export function useTypeActiveOrIncludedFilterFn(
  operatorTicket: OperatorTicket
): TicketTypeFilterFn {
  const { ticketTypesStore } = useStores();

  if (!operatorTicket) {
    return () => null;
  }

  const { ticketTypeId, subTicketTypeIds, kind } = operatorTicket;
  // collect ids which should pass filter
  const passTypeId = new Set<number>([ticketTypeId, ...subTicketTypeIds]);

  // make sure parentId is included (in case of custom subtype, ticketTypeId is category id)
  const [subTypeId] = subTicketTypeIds;
  const subTypeNode = ticketTypesStore.getTicketTypeByKind(subTypeId, kind);
  if (!passTypeId.has(subTypeNode.parent.id)) {
    passTypeId.add(subTypeNode.parent.id);
  }

  return (node) => node.isActive || (node.kind === kind && passTypeId.has(node.id));
}

export type ItemCategoryId =
  | typeof TICKET_TYPE_OVERDUE_ID
  | typeof TICKET_TYPE_EPISODES_ID
  | typeof TICKET_TYPE_NON_EPISODES_ID
  | typeof TICKET_TYPE_CALLBACK_ID
  | typeof TICKET_TYPE_ORAL_ONCO_REPORT_ID
  | typeof TICKET_TYPE_ORAL_ONCO_OVERDUE_ID
  | typeof TICKET_TYPE_SYMPTOM_ASSESSMENT_REPORT_ID;

export const itemCategoriesMap: Record<string, { label: string; featureFlag: FEATURES }> = {
  [TICKET_TYPE_ORAL_ONCO_REPORT_ID]: {
    label: 'Oral Oncolytics Assessment',
    featureFlag: FEATURES.ORAL_ONCOLYTICS_ASSESSMENT
  },
  [TICKET_TYPE_SYMPTOM_ASSESSMENT_REPORT_ID]: {
    label: 'Symptom Assessment',
    featureFlag: FEATURES.SYMPTOM_ASSESSMENT
  },
  [TICKET_TYPE_CALLBACK_ID]: {
    label: 'Callback Requests',
    featureFlag: FEATURES.CALLBACK_REQUESTS
  },
  [TICKET_TYPE_EPISODES_ID]: {
    label: 'Episode Tasks',
    featureFlag: FEATURES.EPISODE_TASKS
  },
  [TICKET_TYPE_NON_EPISODES_ID]: {
    label: 'Non-Episode Tasks',
    featureFlag: FEATURES.NON_EPISODE_TASKS
  },
  [TICKET_TYPE_OVERDUE_ID]: {
    label: 'Overdue Symptom Assessment',
    featureFlag: FEATURES.OVERDUE_SYMPTOM_ASSESSMENT
  },
  [TICKET_TYPE_ORAL_ONCO_OVERDUE_ID]: {
    label: 'Overdue Oral Oncolytics Assessment',
    featureFlag: FEATURES.OVERDUE_ORAL_ONCOLYTICS_ASSESSMENT
  }
};

export const getItemCategoriesOptions = (
  settingsStore: SettingsStore,
  order: ItemCategoryId[] = [
    TICKET_TYPE_ORAL_ONCO_REPORT_ID,
    TICKET_TYPE_SYMPTOM_ASSESSMENT_REPORT_ID,
    TICKET_TYPE_CALLBACK_ID,
    TICKET_TYPE_EPISODES_ID,
    TICKET_TYPE_NON_EPISODES_ID,
    TICKET_TYPE_OVERDUE_ID,
    TICKET_TYPE_ORAL_ONCO_OVERDUE_ID
  ]
): TicketSubTypeOption[] => {
  const options: TicketSubTypeOption[] = [];

  order.forEach((id) => {
    if (settingsStore.hasFeature(itemCategoriesMap[id].featureFlag)) {
      options.push({
        label: itemCategoriesMap[id].label,
        value: id,
        parentId: null,
        parentName: null
      });
    }
  });

  return options;
};

export function useTicketTypesOptions(options?: TicketTypesOptions): ITicketTypeSelectOptions[] {
  const { settingsStore } = useStores();
  options = { ...ticketTypesOptionsDefault, ...options };
  const {
    filterFn,
    addCategoryAllOption,
    addItemCategories,
    itemCategoryIdsOrder,
    addTicketTypesAndSubTypes = true,
    showFullNameForNonSymptomTicketTypes = false
  } = options;
  const { ticketTypesStore } = useStores();

  return useMemo(() => {
    let options: ITicketTypeSelectOptions[] = [];

    if (addTicketTypesAndSubTypes) {
      options = getGroupedTicketTypesForSelect(
        ticketTypesStore.ticketTypesTree,
        filterFn,
        showFullNameForNonSymptomTicketTypes
      );
      if (addCategoryAllOption) {
        options = options.map((ticketCategoryOption: ITicketTypeSelectOptions) => {
          const finalOptions = [...ticketCategoryOption.options];
          if (ticketCategoryOption.options.length > 1) {
            finalOptions.unshift({
              label: `All: ${ticketCategoryOption.label}`,
              parentId: null,
              parentName: null,
              value: `${ticketCategoryOption.id}`
            });
          }
          return {
            ...ticketCategoryOption,
            options: finalOptions
          };
        });
      }
    }

    if (addItemCategories) {
      const itemCategoriesOptions = getItemCategoriesOptions(settingsStore, itemCategoryIdsOrder);

      options.unshift({
        id: -1,
        label: 'Item Categories',
        kind: null,
        options: itemCategoriesOptions
      });
    }
    return options;
  }, [
    addCategoryAllOption,
    addItemCategories,
    addTicketTypesAndSubTypes,
    ticketTypesStore,
    settingsStore,
    filterFn,
    showFullNameForNonSymptomTicketTypes,
    itemCategoryIdsOrder
  ]);
}

// get Locations options
export function useTicketLocationsOptions() {
  const { locationsStore } = useStores();
  return locationsStore.locationsForSelect;
}

// urgency options
export const urgencyOptions = [
  { value: OperatorTicketUrgency.Standard, label: OperatorTicketUrgencyText.Standard },
  { value: OperatorTicketUrgency.High, label: OperatorTicketUrgencyText.High }
];

const symptomUrgencyOptions: SymptomTicketUrgencyValueAndText[] = [
  nurseReviewUrgency,
  attentionTodayUrgency,
  immediateAttentionUrgency
];

export const getUrgencyDefaultValue = (isSymptom: boolean) =>
  isSymptom ? defaultSymptomUrgencyValue : defaultNonSymptomUrgencyValue;

export const getUrgencyOptions = (isSymptom: boolean) =>
  isSymptom ? symptomUrgencyOptions : urgencyOptions;

export const ticketTypesSelectCustomFilter = (option: any, searchText: string) => {
  const searchValue = searchText.toLowerCase();
  return (
    option.label.toLowerCase().includes(searchValue) ||
    (Boolean(option.data.parentId) && option.data.parentName.toLowerCase().includes(searchValue))
  );
};

export const disableTicketTypeSelectFilter = () => true; // Disable default filtering if we're doing it ourselves

interface SymptomAlertSectionProps {
  title: string;
  alertText: AlertText;
}

export const SymptomAlertSection: FC<SymptomAlertSectionProps> = ({ title, alertText }) => {
  return (
    <StyledOperatorTicketSectionContainer px={12} py={20}>
      <Text textTransform="uppercase" color="secondary" variant="body2" mb={20} component="div">
        {title}
      </Text>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Text variant="h4" component="div">
          Ticket urgency escalated due to the severity of the symptom
        </Text>
        <AlertBadge alertText={alertText} />
      </Box>
    </StyledOperatorTicketSectionContainer>
  );
};

export const StyledOperatorTicketSectionContainer = styled(Box)(
  ({ theme }) => css`
    background-color: ${theme.palette.natural.white};
    border: 1px solid ${theme.palette.natural.border};
    border-bottom: 0;
  `
);

/**
 * Hook that provides search functionality for ticket types using useSearchAndHighlight
 * This replaces the old filterOption logic with keyword-based search
 */
export const useTicketTypeSearch = (
  groupedTicketTypesOptions: ITicketTypeSelectOptions[],
  searchValue: string,
  selectedIds: string[]
): TicketTypeSearchFilteredOptions[] => {
  const groups = useMemo(
    () =>
      groupedTicketTypesOptions.map<SearchableGroup<TicketSubTypeOption, ITicketTypeSelectOptions>>(
        (group) => ({
          label: group.label,
          data: group,
          items: group.options.map((item) => ({
            name: item.label,
            data: item,
            keywords: item.keywords?.slice() // convert mobx proxy to js array
          }))
        })
      ),
    [groupedTicketTypesOptions]
  );

  const searchResults = useSearchAndHighlight({
    groups,
    searchValue
  });

  // Transform search results back to the format expected by FormTicketTypeMultiAutocomplete
  return useMemo(() => {
    if (!searchValue.trim()) {
      return groupedTicketTypesOptions; // Show all options when no search
    }

    return searchResults.map((group) => ({
      ...group.data,
      options: group.items
        .map((item) => ({ ...item.item.data, searchResult: item.searchResult }))
        .filter((option) => !selectedIds.includes(option.value)) // Filter out already selected options
    }));
  }, [searchResults, groupedTicketTypesOptions, searchValue, selectedIds]);
};

export const CustomTicketTypeOption: FC<OptionProps<TicketSubTypeOptionWithSearch, boolean>> = (
  props
) => {
  return (
    <DefaultMenuOption
      {...props}
      children={
        props.data.searchResult ? (
          <HighlightBySearchResult {...props.data.searchResult} />
        ) : (
          props.children
        )
      }
    />
  );
};
